// Single source of truth for the canonical SEO domain.
// Used by every <SEO> component, JSON-LD generator and sitemap reference.
// If you ever change the primary domain, change it here.
export const CANONICAL_ORIGIN = "https://openseafishingtackles.com";
export const STORE_NAME = "Opensea Fishing Tackles";
export const STORE_DESCRIPTION =
    "Premium fishing gear for Indian anglers. Rods, reels, lures and tackle — handpicked, authentic, and shipped pan-India.";
export const DEFAULT_OG_IMAGE =
    "https://customer-assets.emergentagent.com/job_98d63437-58f0-458d-ad8f-bdf2ebbc1d64/artifacts/agswg8ik_Opensea_PNG_Logo.png";

export function canonicalFor(path) {
    if (!path) path = "/";
    if (!path.startsWith("/")) path = `/${path}`;
    return `${CANONICAL_ORIGIN}${path}`;
}

// Build an absolute image URL on the canonical domain. Accepts either a
// fully-qualified URL (returned as-is) or a relative API path like
// `/api/images/<id>`. Optionally appends a variant.
export function absoluteImage(src, variant) {
    if (!src) return DEFAULT_OG_IMAGE;
    if (/^https?:\/\//.test(src)) return src;
    const base = `${CANONICAL_ORIGIN}${src.startsWith("/") ? src : `/${src}`}`;
    if (variant && src.includes("/api/images/")) {
        const sep = base.includes("?") ? "&" : "?";
        return `${base}${sep}v=${encodeURIComponent(variant)}`;
    }
    return base;
}

// ---------- JSON-LD generators ----------
export function organizationJsonLd(settings) {
    const sameAs = [];
    const ig = settings?.instagram_url;
    if (ig) sameAs.push(ig);
    return {
        "@context": "https://schema.org",
        "@type": "OnlineStore",
        name: STORE_NAME,
        url: CANONICAL_ORIGIN,
        logo: DEFAULT_OG_IMAGE,
        description: STORE_DESCRIPTION,
        ...(sameAs.length ? { sameAs } : {}),
        ...(settings?.support_phone || settings?.whatsapp_number
            ? {
                  contactPoint: {
                      "@type": "ContactPoint",
                      telephone: settings.support_phone || settings.whatsapp_number,
                      contactType: "customer service",
                      areaServed: "IN",
                  },
              }
            : {}),
    };
}

export function websiteJsonLd() {
    return {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: STORE_NAME,
        url: CANONICAL_ORIGIN,
        potentialAction: {
            "@type": "SearchAction",
            target: `${CANONICAL_ORIGIN}/shop?q={search_term_string}`,
            "query-input": "required name=search_term_string",
        },
    };
}

export function breadcrumbJsonLd(items) {
    // items: [{ name, path }]
    return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: items.map((it, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: it.name,
            item: canonicalFor(it.path),
        })),
    };
}

// Product JSON-LD per schema.org/Product with Offer.
// We deliberately do NOT emit aggregateRating / review until real review data exists.
export function productJsonLd(product, displayPrice) {
    if (!product) return null;
    const imgList = [];
    if (product.primary_image) imgList.push(absoluteImage(product.primary_image, "large"));
    (product.images || []).forEach((src) => {
        const a = absoluteImage(src, "large");
        if (!imgList.includes(a)) imgList.push(a);
    });
    const inStock = (Number(product.stock || 0) > 0)
        || ((product.variants || []).some((v) => v.active && Number(v.stock || 0) > 0));
    const price = Number(
        displayPrice ??
            product.sale_price ??
            product.price ??
            (product.variants && product.variants[0]?.sale_price) ??
            (product.variants && product.variants[0]?.price) ??
            0
    );
    const sku =
        product.sku ||
        (product.variants && product.variants[0]?.sku) ||
        product.id ||
        product.slug;

    const doc = {
        "@context": "https://schema.org",
        "@type": "Product",
        name: product.name,
        description:
            product.short_description ||
            product.description ||
            `${product.name} — buy online from ${STORE_NAME}.`,
        image: imgList.length ? imgList : [DEFAULT_OG_IMAGE],
        sku: String(sku),
        url: canonicalFor(`/product/${product.slug}`),
    };
    if (product.brand?.name) {
        doc.brand = { "@type": "Brand", name: product.brand.name };
    }
    if (product.category?.name) {
        doc.category = product.category.name;
    }
    doc.offers = {
        "@type": "Offer",
        url: canonicalFor(`/product/${product.slug}`),
        priceCurrency: "INR",
        price: price.toFixed(2),
        availability: inStock
            ? "https://schema.org/InStock"
            : "https://schema.org/OutOfStock",
        itemCondition: "https://schema.org/NewCondition",
        seller: { "@type": "Organization", name: STORE_NAME },
    };
    return doc;
}

// ---------- Title / description templates ----------
export function productSeoTitle(product) {
    if (product?.seo_title) return product.seo_title;
    return `Buy ${product.name} Online in India | ${STORE_NAME}`;
}

export function productSeoDescription(product) {
    if (product?.seo_description) return product.seo_description;
    const base = product.short_description || product.description || "";
    if (base) {
        return `Shop ${product.name} for fishing in India. ${base.slice(0, 130)}`.slice(0, 160);
    }
    return `Shop ${product.name} for fishing in India. View price, specs, availability and order online from ${STORE_NAME}.`;
}

export function categorySeoTitle(name) {
    return `Buy ${name} Online in India | ${STORE_NAME}`;
}
export function categorySeoDescription(name) {
    return `Explore ${name} for Indian anglers. Shop fishing rods, reels, lures and tackle online from ${STORE_NAME}.`;
}
export function brandSeoTitle(name) {
    return `Buy ${name} Fishing Tackle in India | ${STORE_NAME}`;
}
export function brandSeoDescription(name) {
    return `Shop ${name} fishing tackle — rods, reels, lures and accessories — online from ${STORE_NAME}. Authentic gear, shipped pan-India.`;
}
